<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNavigation } from '@/composables/useNavigation'
import type { NavigationMenuItem } from './types'

const props = defineProps<{
  item: NavigationMenuItem
}>()

const emit = defineEmits<{
  select: [item: NavigationMenuItem]
}>()

const { t } = useI18n()
const { getMenuItemTo } = useNavigation()

function getChildren(item: NavigationMenuItem) {
  return item.sub_menu ?? item.list_menu ?? item.children ?? []
}

const hasChildren = computed(() => getChildren(props.item).length > 0)

function onSelect(item: NavigationMenuItem) {
  emit('select', item)
}

function displayName(item: NavigationMenuItem) {
  return t(item.locale_name ?? item.name ?? '')
}
</script>

<template>
  <v-list-item
    v-if="!hasChildren"
    class="text-body-medium"
    :title="displayName(props.item)"
    :to="getMenuItemTo(props.item)"
    @click="onSelect(props.item)"
  />

  <v-list-group
    v-else
    :value="props.item.locale_name"
  >
    <template #activator="{ props: activatorProps, isOpen }">
      <v-list-item
        v-bind="activatorProps"
        class="text-body-medium"
        :title="displayName(props.item)"
      >
        <template #append>
          <v-icon
            class="chevron-rotate"
            :class="{ 'chevron-rotate--active': isOpen }"
            icon="mdi-chevron-down"
          />
        </template>
      </v-list-item>
    </template>

    <template
      v-for="(sub, index) in getChildren(props.item)"
      :key="props.item.locale_name + '-' + sub.locale_name + '-' + index"
    >
      <v-list-item
        v-if="getChildren(sub).length === 0"
        class="text-body-medium"
        :title="displayName(sub)"
        :to="getMenuItemTo(sub)"
        @click="onSelect(sub)"
      />

      <v-list-group
        v-else
        :value="sub.locale_name"
      >
        <template #activator="{ props: subActivatorProps, isOpen: subIsOpen }">
          <v-list-item
            v-bind="subActivatorProps"
            class="text-body-medium"
            :title="displayName(sub)"
          >
            <template #append>
              <v-icon
                class="chevron-rotate"
                :class="{ 'chevron-rotate--active': subIsOpen }"
                icon="mdi-chevron-down"
              />
            </template>
          </v-list-item>
        </template>

        <v-list-item
          v-for="leaf in getChildren(sub)"
          :key="leaf.locale_name"
          class="text-body-medium pl-12"
          :title="displayName(leaf)"
          :to="getMenuItemTo(leaf)"
          @click="onSelect(leaf)"
        />
      </v-list-group>
    </template>
  </v-list-group>
</template>

<style scoped>
/* Override Vuetify's default indent padding for cleaner mobile menu */
:deep(.v-list-group__items) {
  --indent-padding: 16px;
}
</style>
